<script>
import Layout from "../../layouts/main";
import Style from "@/views/layouts/style";
import PageHeader from "@/components/Page-header";
import appConfig from "@/../app.config";
import perfilSociodemograficoAPI from "../../../services/standards/perfilSociodemografico/perfilSociodemograficoAPI";

// Paleta categórica (barras "distribuidas": un color por categoría).
const PALETTE = ["#3b76ef", "#f7b84b", "#4fc6e1", "#f1556c", "#5b69bc", "#43d39e", "#f9c851", "#8592a3", "#ef6c00", "#00897b"];

/**
 * 3.1.1 — Reporte del perfil sociodemográfico. Gráficas de BARRAS (ApexCharts) con la cantidad
 * y el % rotulados en cada barra. Los datos (conteos por categoría) vienen de un único llamado a
 * reportDiagram; los porcentajes se calculan en el front sobre el total de cada grupo.
 */
export default {
  page: {
    title: "Perfil sociodemográfico y condiciones de salud",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    Style,
    PageHeader,
  },
  data() {
    return {
      title: "3.1.1 Perfil sociodemográfico y condiciones de salud ",
      items: [
        { text: "Estandares", href: "/forms/estandares" },
        {
          text: "Perfil sociodemográfico y condiciones de salud ",
          href: `/forms/estandares/${this.$store.getters["company/getCompany"].id || null}/perfil-sociodemografico`,
        },
        { text: "Reportes", active: true },
      ],
      raw: null,
      loading: true,
      excel: null,
      companyID: this.$store.getters["company/getCompany"].id || null,
      classificationID: this.$store.getters["company/getCompany"].company_classification_id || null,
      user: this.$store.state.auth || null,
    };
  },
  computed: {
    /** Total de colaboradores analizados (suma del grupo Género). */
    totalColaboradores() {
      const g = (this.raw && this.raw.Genero) || {};
      return (g.Masculino || 0) + (g.Femenino || 0);
    },

    /** Definición declarativa de cada gráfica (título + barras cantidad/%). */
    chartList() {
      const r = this.raw;
      if (!r) return [];
      const G = r.Genero || {};
      const C = r.Composicion_familiar || {};
      const V = r.Tipo_vinculacion || {};
      const E = r.Estado_civil || {};
      const S = r.Estrato_socioeconomico || {};
      const Es = r.Grado_escolaridad || {};
      const res = Object.values(r.Lugar_residencia || {});

      const list = [
        { title: "Género", ...this.makeBar(["Femenino", "Masculino"], [G.Femenino || 0, G.Masculino || 0]) },
        { title: "Composición familiar (personas a cargo)", ...this.makeBar(["Cero", "Uno o dos", "Tres o más"], [C.Cero || 0, C.Uno_o_Dos || 0, C.Tres_o_Mas || 0]) },
        { title: "Tipo de vinculación", ...this.makeBar(["Laboral", "Estudiante", "Contratista"], [V.Laboral || 0, V.Estudiante || 0, V.Contratista || 0]) },
        { title: "Estado civil", ...this.makeBar(["Casado", "Soltero", "Unión libre", "Viudo"], [E.Casado || 0, E.Soltero || 0, E.UnionLibre || 0, E.Viudo || 0]) },
        { title: "Estrato socioeconómico", ...this.makeBar(["1", "2", "3", "4", "5", "6"], [S.Uno || 0, S.Dos || 0, S.Tres || 0, S.Cuatro || 0, S.Cinco || 0, S.Seis || 0]) },
        { title: "Grado de escolaridad", ...this.makeBar(["Primaria", "Secundaria", "Técnica y Tecnología", "Universitaria", "Posgrado"], [Es.Primaria || 0, Es.Secundaria || 0, Es.tecnica_tecnologia || 0, Es.Universitaria || 0, Es.Posgrado || 0]) },
      ];

      if (res.length) {
        list.push({
          title: "Lugar de residencia",
          full: true,
          ...this.makeBar(res.map((c) => c.Ciudad || "—"), res.map((c) => c.Cantidad || 0), { horizontal: true }),
        });
      }
      return list;
    },
  },
  methods: {
    /**
     * Construye la config ApexCharts de una gráfica de barras con etiqueta "cantidad (%)".
     * @returns {{series:Array, options:Object, height:Number, full?:Boolean}}
     */
    makeBar(labels, values, { horizontal = false } = {}) {
      const total = values.reduce((a, b) => a + (Number(b) || 0), 0);
      const pct = (val) => (total ? Math.round((val / total) * 100) : 0);
      const height = horizontal ? Math.max(320, labels.length * 42 + 60) : 330;
      // En barras horizontales las categorías (texto) caen en el eje Y; redondear solo números
      // y dejar pasar el texto evita el "NaN" al aplicar Math.round a un nombre de ciudad.
      const intOrLabel = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? Math.round(n) : v;
      };

      return {
        height,
        series: [{ name: "Colaboradores", data: values }],
        options: {
          chart: { type: "bar", height, toolbar: { show: false }, fontFamily: "inherit" },
          plotOptions: {
            bar: {
              distributed: true,
              horizontal,
              borderRadius: 4,
              columnWidth: "55%",
              barHeight: "70%",
              dataLabels: { position: horizontal ? "center" : "top" },
            },
          },
          colors: PALETTE,
          dataLabels: {
            enabled: true,
            offsetY: horizontal ? 0 : -22,
            style: { fontSize: "11px", fontWeight: 600, colors: [horizontal ? "#fff" : "#444"] },
            formatter: (val) => (val ? `${val} (${pct(val)}%)` : "0"),
          },
          legend: { show: false },
          grid: { borderColor: "#f1f1f1" },
          xaxis: {
            categories: labels,
            labels: { style: { fontSize: "12px" }, rotate: horizontal ? 0 : -20, trim: true, formatter: intOrLabel },
            title: { text: horizontal ? "N.° de colaboradores" : "" },
          },
          yaxis: {
            title: { text: horizontal ? "" : "N.° de colaboradores" },
            labels: { formatter: intOrLabel },
          },
          tooltip: {
            y: {
              formatter: (val) => `${val} colaborador(es) — ${pct(val)}% del total`,
            },
          },
          noData: { text: "Sin datos registrados" },
        },
      };
    },

    getGrafica() {
      perfilSociodemograficoAPI
        .getGrafica(this.companyID)
        .then((response) => {
          this.raw = response || null;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    async print() {
      try {
        perfilSociodemograficoAPI.getExcel(this.companyID).then((response) => {
          this.excel = response;
          window.location.href = this.excel;
        });
      } catch (error) {
        console.log(error);
      }
    },

    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.getGrafica();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-md-12 m-0 p-0">
        <div class="card">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-6">
                <h5 class="mb-0">
                  Total de colaboradores analizados:
                  <span class="badge badge-primary" style="font-size: 14px;">{{ totalColaboradores }}</span>
                </h5>
              </div>
              <div class="col-6 text-right m-b-0">
                <button @click="print" type="button" class="btn btn-primary mx-1" id="tooltip">
                  <span class="btn-label"><i class="bx bx-download"></i></span>
                  Descripción sociodemográfica de los colaboradores
                </button>
                <div class="btn btn-secondary m-l-5 ml-1" id="back" @click="goBack">
                  <i class="fas fa-arrow-left"></i>
                </div>
                <b-tooltip target="tooltip" placement="left">Descargar</b-tooltip>
                <b-tooltip target="back" placement="left">Volver</b-tooltip>
              </div>
            </div>

            <hr />

            <div v-if="!loading" class="row">
              <div
                v-for="(c, i) in chartList"
                :key="i"
                :class="c.full ? 'col-12' : 'col-lg-6'"
                class="pt-2"
              >
                <div class="card h-100">
                  <div class="card-body" dir="ltr">
                    <h4 class="header-title mb-2">{{ c.title }}</h4>
                    <apexchart type="bar" :height="c.height" :options="c.options" :series="c.series"></apexchart>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.background-modal {
  background-color: transparent !important;
  border: none;
}
</style>
